import type { Query, QueryClient, QueryKey } from "react-query";

import type { AssetCommentThreadsQuery } from "src/graphql";

/** One thread as the list query returns it. */
export type CachedThread =
  AssetCommentThreadsQuery["asset_comment_threads"][number];

/** The query-key root of every thread list. */
export const COMMENT_THREAD_LIST_QUERY_KEY = "AssetCommentThreads";

/**
 * Every cached thread list, whatever subject it was fetched for — the same
 * thread sits in more than one, and patching only the visible list leaves the
 * others to flash the old value when they come back into view.
 */
const THREAD_LISTS = {
  predicate: ({ queryKey }: Query) =>
    queryKey[0] === COMMENT_THREAD_LIST_QUERY_KEY,
};

/** What {@link patchCachedThread} took out, for putting back on failure. */
export type ThreadListSnapshot = [
  QueryKey,
  AssetCommentThreadsQuery | undefined,
][];

/**
 * Writes `patch` over one thread in every cached list, returning what was
 * there before — for the mutations that have to show on the click rather than
 * a round trip later. In-flight polls are cancelled first: one landing after
 * this would put the server's pre-mutation answer back over the top.
 */
export async function patchCachedThread(
  queryClient: QueryClient,
  threadId: string,
  patch: (thread: CachedThread) => CachedThread,
): Promise<ThreadListSnapshot> {
  await queryClient.cancelQueries(THREAD_LISTS);

  const previous =
    queryClient.getQueriesData<AssetCommentThreadsQuery>(THREAD_LISTS);

  queryClient.setQueriesData<AssetCommentThreadsQuery | undefined>(
    THREAD_LISTS,
    (data) =>
      data && {
        ...data,
        asset_comment_threads: data.asset_comment_threads.map((thread) =>
          thread.id === threadId ? patch(thread) : thread,
        ),
      },
  );

  return previous;
}

/** Puts back what {@link patchCachedThread} replaced. */
export function restoreCachedThreads(
  queryClient: QueryClient,
  snapshot: ThreadListSnapshot | undefined,
): void {
  for (const [key, data] of snapshot ?? []) {
    queryClient.setQueryData(key, data);
  }
}

/**
 * Refetches the thread lists and nothing else.
 *
 * Narrower than invalidating every comment query: read state and pin positions
 * can't change the open-thread count, so there's no reason to refetch that
 * aggregate along with them.
 */
export function invalidateThreadLists(queryClient: QueryClient): void {
  void queryClient.invalidateQueries(THREAD_LISTS);
}
